import { useMemo } from 'react';
import { ProgressStep } from '@monorepo/types';
import { useStore } from '@hub/helpers/use-store';
import { calculateProgramProgression } from '../utils/progression-calculations';

/**
 * Frontend hook to calculate program progression chain for a given program ID
 * Replaces the backend API call with client-side calculation using existing program store data
 */
export const useProgramProgression = (
  programId: string,
): {
  data: ProgressStep[] | undefined;
  groupInfo?: { groupId: string; groupName: string };
  isLoading: boolean;
  isError: boolean;
} => {
  const {
    dataStore: { programStore },
  } = useStore();

  const result = useMemo(() => {
    // If no programId provided, return empty
    if (!programId) {
      return {
        data: [],
        groupInfo: undefined,
        isLoading: false,
        isError: false,
      };
    }

    try {
      // Calculate progression using all available programs from store
      const progressionData = calculateProgramProgression(
        programStore.items,
        programId,
      );

      // Find the current program to get group info
      const currentProgram = programStore.items.find(
        (p) => p._id === programId,
      );

      const groupInfo = currentProgram
        ? {
            groupId: currentProgram.groupId || 'N/A',
            groupName: currentProgram.groupName || 'N/A',
          }
        : undefined;

      return {
        data: progressionData,
        groupInfo,
        isLoading: false,
        isError: false,
      };
    } catch (error) {
      console.error('Error calculating program progression:', error);
      return {
        data: undefined,
        groupInfo: undefined,
        isLoading: false,
        isError: true,
      };
    }
  }, [programStore.items, programStore.isAllFetched, programId]);

  return result;
};
